import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { TemplateScope } from '../../../../extViewmodels/sdk/models/SurveyTemplate';
import { AdminDefaultSupportedIndustries } from '../../../../models';
import { getIndustryName } from '../../../../models/UiUtils';
import {
	useAddTemplateToIndustryMutation,
	useCreateSurveyTemplateMutation,
	useReplaceTemplateIndustriesMutation,
	useUpdateSurveyTemplateMutation,
} from '../../../../queries/SurveyTemplate';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { Pill } from '../../../../web/components-v2/Pill/Pill';
import { THEME, ThemeColors } from '../../../../web/components-v2/Theme/theme';

export interface ISaveSurveyTemplateModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	surveyDefinition: Api.ICustomSurvey;
	onSuccess?: (templateId: string) => void;
	/** When provided, modal will update existing template instead of creating new one */
	templateId?: string;
	/** Initial template name (used when editing) */
	initialTemplateName?: string;
	/** Initial industries the template is associated with (used when editing) */
	initialIndustries?: Api.Industry[];
	/** Initial industries with their statuses (used when editing to display status) */
	initialIndustriesWithStatus?: Record<string, Api.TemplateStatus>;
}

// Use the same industries that appear in the Survey Library UI
// Maps AdminDefaultSupportedIndustries to display format
const AVAILABLE_INDUSTRIES = AdminDefaultSupportedIndustries.map(industry => ({
	value: industry,
	label: getIndustryName(industry),
}));

// Maps TemplateStatus to Pill component properties
export const getStatusDisplayInfo = (
	status: Api.TemplateStatus | undefined
): { label: string; color: keyof ThemeColors['pill'] } | null => {
	if (status === undefined) return null;

	switch (status) {
		case Api.TemplateStatus.Published:
			return { label: 'Published', color: 'green' };
		case Api.TemplateStatus.Draft:
			return { label: 'Draft', color: 'yellow' };
		case Api.TemplateStatus.Disabled:
			return { label: 'Disabled', color: 'gray' };
		case Api.TemplateStatus.Archived:
			return { label: 'Archived', color: 'gray' };
		default:
			return null;
	}
};

/**
 * Modal for creating/updating survey templates with industry assignments.
 *
 * Supports two workflows:
 * 1. **Save (Draft):** Creates/updates template and preserves existing industry statuses.
 *    New industries are added as Draft. Archived industries are restored as Draft.
 * 2. **Publish:** Creates/updates template and sets ALL industries to Published status
 *    using a single optimized API call.
 *
 * @example
 * // Creating a new template
 * <SaveSurveyTemplateModal
 *   open={true}
 *   onOpenChange={setOpen}
 *   surveyDefinition={definition}
 *   onSuccess={(id) => navigate(`/templates/${id}`)}
 * />
 *
 * @example
 * // Editing existing template
 * <SaveSurveyTemplateModal
 *   open={true}
 *   onOpenChange={setOpen}
 *   surveyDefinition={definition}
 *   templateId='existing-id'
 *   initialTemplateName='Original Name'
 *   initialIndustries={[Industry.RealEstate, Industry.Mortgage]}
 *   initialIndustriesWithStatus={{ [Industry.RealEstate]: TemplateStatus.Published }}
 *   onSuccess={(id) => navigate(`/templates/${id}`)}
 * />
 */
export function SaveSurveyTemplateModal({
	open,
	onOpenChange,
	surveyDefinition,
	onSuccess,
	templateId,
	initialTemplateName = '',
	initialIndustries = [],
	initialIndustriesWithStatus,
}: ISaveSurveyTemplateModalProps) {
	const isEditMode = !!templateId;
	const [templateName, setTemplateName] = React.useState(initialTemplateName);
	const [selectedIndustries, setSelectedIndustries] = React.useState<Set<Api.Industry>>(new Set(initialIndustries));
	const [error, setError] = React.useState<string | null>(null);

	const createMutation = useCreateSurveyTemplateMutation({
		onError: err => {
			setError(err.systemMessage || 'Failed to create template');
		},
	});

	const updateMutation = useUpdateSurveyTemplateMutation({
		onError: err => {
			setError(err.systemMessage || 'Failed to update template');
		},
	});

	const addToIndustryMutation = useAddTemplateToIndustryMutation();

	const replaceIndustriesMutation = useReplaceTemplateIndustriesMutation({
		onError: err => {
			setError(err.systemMessage || 'Failed to update industries');
		},
	});

	// Initialize form when modal opens, reset when it closes
	React.useEffect(() => {
		if (open) {
			setTemplateName(initialTemplateName);
			setSelectedIndustries(new Set(initialIndustries));
			setError(null);
		} else {
			// Reset form when modal closes
			setTemplateName('');
			setSelectedIndustries(new Set());
			setError(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const nameCharsRemaining = 100 - templateName.length;
	const isValid = templateName.trim().length > 0 && selectedIndustries.size > 0;
	const isSaving =
		createMutation.isPending ||
		updateMutation.isPending ||
		addToIndustryMutation.isPending ||
		replaceIndustriesMutation.isPending;

	const handleIndustryToggle = (industry: Api.Industry) => {
		setSelectedIndustries(prev => {
			const next = new Set(prev);
			if (next.has(industry)) {
				next.delete(industry);
			} else {
				next.add(industry);
			}
			return next;
		});
	};

	/**
	 * Handle saving a template (Update Template / Save Template button).
	 *
	 * **Create Mode:** Uses single replaceIndustriesMutation call to set all industries as Draft.
	 * **Edit Mode:** Preserves existing industry statuses and only adds new industries as Draft.
	 */
	const handleSave = async () => {
		if (!isValid || isSaving) {
			return;
		}

		setError(null);
		const status = Api.TemplateStatus.Draft;

		try {
			// Step 1: Create or update the template
			const template = isEditMode
				? await updateMutation.mutateAsync({
						id: templateId!,
						name: templateName.trim(),
						surveyDefinition,
					})
				: await createMutation.mutateAsync({
						name: templateName.trim(),
						summary: '', // No longer collecting summary from user
						surveyDefinition,
						scope: TemplateScope.System,
						enabled: false, // Start as draft, status is set per industry
					});

			// Step 2: Handle industry associations
			if (template.id && selectedIndustries.size > 0) {
				if (isEditMode) {
					// When editing, preserve existing statuses and only update new industries
					try {
						// Calculate which industries were removed (need soft delete via replace)
						const initialIndustriesSet = new Set(initialIndustries);
						const removedIndustries = Array.from(initialIndustriesSet).filter(ind => !selectedIndustries.has(ind));

						// Step 2a: Only replace industries if any were REMOVED (soft delete)
						if (removedIndustries.length > 0) {
							await replaceIndustriesMutation.mutateAsync({
								templateId: template.id,
								industries: Array.from(selectedIndustries),
								// Don't pass status parameter - this tells the backend to:
								// 1. Keep existing statuses for industries that remain in the list
								// 2. Soft-delete (archive) industries not in the list
								// If we passed status here, ALL remaining industries would be overwritten to that status,
								// which would lose the distinction between Published/Draft industries
							});
						}

						// Step 2b: For newly added industries, set the requested status
						const newlyAddedIndustries = Array.from(selectedIndustries).filter(
							industry => !initialIndustriesSet.has(industry)
						);

						// Step 2c: Restore archived industries as Draft (business rule)
						// Archived industries cannot be "preserved" - they must be explicitly restored.
						// The user's re-selection of an archived industry is treated as intent to restore it.
						// We restore as Draft (not the user's requested status) to force explicit Publishing.
						const archivedIndustries = Array.from(selectedIndustries).filter(
							industry =>
								initialIndustriesSet.has(industry) &&
								initialIndustriesWithStatus?.[industry] === Api.TemplateStatus.Archived
						);

						// Step 2d: Add newly added industries using addToIndustryMutation (POST)
						if (newlyAddedIndustries.length > 0) {
							const addPromises = newlyAddedIndustries.map(industry =>
								addToIndustryMutation
									.mutateAsync({
										industry,
										templateId: template.id,
										status,
									})
									.then(() => ({ industry, success: true, error: null as any }))
									.catch(err => ({ industry, success: false, error: err }))
							);

							const addResults = await Promise.all(addPromises);
							const failures = addResults.filter(r => !r.success);

							if (failures.length > 0) {
								const failedIndustries = failures.map(f => f.industry).join(', ');
								setError(`Failed to assign new industries: ${failedIndustries}`);
								return;
							}
						}

						// Step 2e: Restore archived industries as Draft
						if (archivedIndustries.length > 0) {
							const draftPromises = archivedIndustries.map(industry =>
								addToIndustryMutation
									.mutateAsync({
										industry,
										templateId: template.id,
										status,
									})
									.then(() => ({ industry, success: true, error: null as any }))
									.catch(err => ({ industry, success: false, error: err }))
							);

							const draftResults = await Promise.all(draftPromises);
							const failures = draftResults.filter(r => !r.success);

							if (failures.length > 0) {
								const failedIndustries = failures.map(f => f.industry).join(', ');
								setError(`Failed to restore archived industries: ${failedIndustries}`);
								return;
							}
						}
					} catch (err) {
						console.error('Error updating industries:', err);
						// Error will be caught by onError callback, but we need to prevent closing modal
						return;
					}
				} else {
					// When creating, use single replace call to set all industries as Draft
					// This is simpler and more efficient than individual addToIndustryMutation calls
					await replaceIndustriesMutation.mutateAsync({
						templateId: template.id,
						industries: Array.from(selectedIndustries),
						status, // Draft status for new templates
					});
				}

				// All succeeded
				// Only navigate on creation, not on edit
				if (!isEditMode) {
					onSuccess?.(template.id);
				} else {
					toast.success('Survey template saved successfully');
				}
				handleClose();
			}
		} catch (err) {
			// Error handled in onError callback
			console.error('Error saving template:', err);
		}
	};

	/**
	 * Handle publishing a template (Publish button).
	 * Uses single replaceIndustriesMutation call to set ALL industries to Published.
	 */
	const handlePublishTemplate = async () => {
		if (!isValid || isSaving) {
			return;
		}

		setError(null);

		try {
			// Step 1: Create or update the template
			const template = isEditMode
				? await updateMutation.mutateAsync({
						id: templateId!,
						name: templateName.trim(),
						surveyDefinition,
					})
				: await createMutation.mutateAsync({
						name: templateName.trim(),
						summary: '',
						surveyDefinition,
						scope: TemplateScope.System,
						enabled: false,
					});

			// Step 2: Publish ALL selected industries in a single call
			if (template.id && selectedIndustries.size > 0) {
				await replaceIndustriesMutation.mutateAsync({
					templateId: template.id,
					industries: Array.from(selectedIndustries),
					status: Api.TemplateStatus.Published,
				});

				// All succeeded
				// Only navigate on creation, not on edit
				if (!isEditMode) {
					onSuccess?.(template.id);
				} else {
					toast.success('Survey template published successfully');
				}
				handleClose();
			}
		} catch (err) {
			// Error handled in onError callback
			console.error('Error publishing template:', err);
		}
	};

	const handleClose = () => {
		if (!isSaving) {
			onOpenChange(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[s.dialogContent]}>
				<DialogHeader>
					<DialogTitle>{isEditMode ? 'Update Survey Template' : 'Save Survey Template'}</DialogTitle>
				</DialogHeader>

				<DialogBody styles={[s.dialogBody]}>
					{/* Template Name */}
					<div className={css(s.fieldGroup)}>
						<label htmlFor='template-name' className={css(s.label)}>
							Template Name *
						</label>
						<Input
							id='template-name'
							type='text'
							value={templateName}
							onChange={e => setTemplateName(e.target.value.slice(0, 100))}
							placeholder='Enter template name'
							maxLength={100}
							disabled={isSaving}
						/>
						<div className={css(s.characterCount)}>
							{nameCharsRemaining} character{nameCharsRemaining !== 1 ? 's' : ''} remaining
						</div>
					</div>

					{/* Industries */}
					<div className={css(s.fieldGroup)}>
						<div className={css(s.label)}>Industries * (Select at least one)</div>
						<div className={css(s.industryGrid)}>
							{AVAILABLE_INDUSTRIES.map(industry => {
								const statusInfo =
									isEditMode && initialIndustriesWithStatus
										? getStatusDisplayInfo(initialIndustriesWithStatus[industry.value])
										: null;

								return (
									<label key={industry.value} className={css(s.checkboxLabel)}>
										<input
											type='checkbox'
											checked={selectedIndustries.has(industry.value)}
											onChange={() => handleIndustryToggle(industry.value)}
											className={css(s.checkbox)}
											disabled={isSaving}
										/>
										<span>{industry.label}</span>
										{statusInfo && (
											<Pill variant='label' color={statusInfo.color} label={statusInfo.label} size='small' />
										)}
									</label>
								);
							})}
						</div>
					</div>

					{/* Error Message */}
					{error && <div className={css(s.errorMessage)}>{error}</div>}
				</DialogBody>

				<DialogFooter>
					<Button variant='primary' onClick={handleSave} disabled={!isValid} isLoading={isSaving}>
						{isEditMode ? 'Update Template' : 'Save Template'}
					</Button>
					<Button variant='success' onClick={handlePublishTemplate} disabled={!isValid} isLoading={isSaving}>
						Publish
					</Button>
					<Button variant='secondary' onClick={handleClose} disabled={isSaving}>
						Cancel
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}

const s = StyleSheet.create({
	dialogContent: {
		maxWidth: 600,
		maxHeight: '90vh',
		overflow: 'auto',
	},
	dialogBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: 20,
	},
	fieldGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	label: {
		fontSize: 14,
		fontWeight: 600,
		color: THEME.colors.text.main,
	},
	characterCount: {
		fontSize: 12,
		color: THEME.colors.text.light,
		marginTop: 4,
	},
	industryGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
		gap: 12,
		padding: 12,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: 4,
		maxHeight: 200,
		overflowY: 'auto',
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		cursor: 'pointer',
		fontSize: 14,
		':hover': {
			opacity: 0.8,
		},
	},
	checkbox: {
		cursor: 'pointer',
		width: 16,
		height: 16,
	},
	errorMessage: {
		padding: 12,
		backgroundColor: THEME.colors.danger.light,
		border: `1px solid ${THEME.colors.danger.main}`,
		borderRadius: 4,
		color: THEME.colors.danger.dark,
		fontSize: 14,
	},
});
